<template>
  <DSBaseInput
    :id="props.id"
    ref="baseInputRef"
    v-model="modelValue"
    :type="props.type"
    :name="props.name"
    :placeholder="props.placeholder"
    :inline-edit="props.inlineEdit"
    :disabled="props.disabled"
    :readonly="props.readonly"
    :required="props.required"
    :autocomplete="props.autocomplete"
    :maxlength="props.maxlength"
    :minlength="props.minlength"
    :suffix="props.suffix"
    :has-error="props.hasError"
    :hidden-input="props.hiddenInput"
    :prefix="props.prefix"
    :clearable="props.clearable"
    :aria-label="props.ariaLabel"
    @focus="onFocus"
    @blur="onBlur"
    @suffix:click="onSuffixAction"
    @clear="clear"
  >
    <template
      v-if="slots.suffix"
      #suffix
    >
      <slot name="suffix" />
    </template>
  </DSBaseInput>
</template>

<script setup lang="ts">
import { ref } from "vue";
import DSBaseInput from "../../base_input/components/DSBaseInput.vue";
import type { DSTextInputEmits, DSTextInputProps, DSTextInputSlots } from "../types/dsTextInputTypes";
import { useDSInput } from "@/src/design_system/form_elements/shared/composables/useDSInput";
import { DSTextFieldModelValue } from "@/src/design_system/form_elements/text_field/types/dsTextFieldTypes";
import { DS_BASE_INPUT_TYPE } from "@/src/design_system/form_elements/base_input/types/inputType";

const props = withDefaults(defineProps<DSTextInputProps>(), {
  inlineEdit: false,
  type: DS_BASE_INPUT_TYPE.Text,
  disabled: false,
  readonly: false,
  required: false,
  hasError: false,
  hiddenInput: false,
  clearable: true,
});

const modelValue = defineModel<DSTextFieldModelValue>({ default: "" });

const emit = defineEmits<DSTextInputEmits>();
const slots = defineSlots<DSTextInputSlots>();

const baseInputRef = ref<InstanceType<typeof DSBaseInput>>();

const { focus, blur } = useDSInput(baseInputRef);

const onFocus = (event: FocusEvent) => {
  emit("focus", event);
};

const onBlur = (event: FocusEvent) => {
  emit("blur", event);
};

const clear = () => {
  modelValue.value = "";
};

const onSuffixAction = (event: MouseEvent) => {
  emit("click:suffix-action", event);
};

defineExpose({
  focus,
  blur,
});
</script>
